import type { OrderDetails, OrderGroupDetails } from '@/entities/order/model'
import { useAcknowledgeOrder } from '@/entities/order/lib'
import { useOrderStatusMutation } from '@/entities/order/model'
import { useDisplayStateByStatus, useTmInitialsEnabled } from '@/entities/state/model'
import { useTmInitialsStore } from '@/features/order/ui/initials-capture-modal'
import { useNoBarcodeCompletionConfirm } from '@/features/order/ui/no-barcode-confirmation'
import { useGroupScanState } from './useGroupScanState'
import { isGroupGatedAction, notifyGroupScanIncomplete } from './useGroupTransitionGuard'

/**
 * Moves every order in a group to `action` together (BD-8130).
 *
 * Shared by the group card and the order details modal, so a grouped order moves with its
 * partners whichever control is used. Arrived orders are acknowledged first. A completion
 * asks for team member initials once, applied to every order it actually completed (BD-8114).
 */
export async function moveOrdersTogether({
  orders,
  action,
  updateStatus,
  updateOrderAcknowledgement,
  enqueueInitials,
}: {
  orders: OrderDetails[]
  action: string
  updateStatus: (params: { orderId: string, newStatus: string }) => Promise<unknown>
  updateOrderAcknowledgement: (order: OrderDetails) => Promise<unknown>
  /** Omitted when initials capture is off. */
  enqueueInitials?: (entry: { orderId: string, orderIds?: string[], customerName?: string }) => void
}): Promise<void> {
  const firstOrder = orders[0]
  if (!firstOrder) {
    return
  }

  // For Arrived orders, acknowledge all orders first before updating status
  if (firstOrder.status === 'Arrived') {
    const unacknowledgedOrders = orders.filter(order => !order.isAcknowledged)

    if (unacknowledgedOrders.length > 0) {
      const acknowledgeResults = await Promise.allSettled(
        unacknowledgedOrders.map(order => updateOrderAcknowledgement(order)),
      )
      const acknowledgeErrors = acknowledgeResults
        .filter(result => result.status === 'rejected')
        .map(result => (result as PromiseRejectedResult).reason)

      if (acknowledgeErrors.length > 0) {
        console.error('Some acknowledgements failed:', acknowledgeErrors)
      }
    }
  }

  // Then update status for all orders
  const statusUpdateResults = await Promise.allSettled(orders.map(order => updateStatus({
    orderId: order.orderId,
    newStatus: action,
  })))
  const statusUpdateErrors = statusUpdateResults
    .filter(result => result.status === 'rejected')
    .map(result => (result as PromiseRejectedResult).reason)

  if (statusUpdateErrors.length > 0) {
    console.error('Some status updates failed:', statusUpdateErrors)
  }

  // BD-8114: a group completion prompts for team member initials exactly once,
  // applied to every order that was actually completed by this action.
  if (action === 'Completed' && enqueueInitials) {
    const completedOrderIds = orders
      .filter((_, index) => statusUpdateResults[index].status === 'fulfilled')
      .map(order => order.orderId)

    if (completedOrderIds.length > 0) {
      enqueueInitials({
        orderId: completedOrderIds[0],
        orderIds: completedOrderIds,
        customerName: firstOrder.customerName,
      })
    }
  }
}

/**
 * Hook to get quick actions for a group of orders.
 * These are presented as buttons on the group order card.
 *
 * @param groupOrder - The group of orders to get quick actions for
 * @returns An object containing the primary action and handlers for it
 */
export function useGroupQuickActions(groupOrder: OrderGroupDetails) {
  const { data: quickButtonStates } = useDisplayStateByStatus()
  const { mutateAsync: updateStatus } = useOrderStatusMutation()
  const { updateOrderAcknowledgement } = useAcknowledgeOrder()
  const isTmInitialsEnabled = useTmInitialsEnabled()
  const enqueueInitials = useTmInitialsStore(state => state.enqueue)
  const scanState = useGroupScanState(groupOrder.orders)
  const { requiresConfirm, requestConfirm } = useNoBarcodeCompletionConfirm()

  async function handleAction(action: string) {
    // BD-8186: the group stays in Arrived until every order in it is fully scanned.
    // Action-keyed like every other route, so OnTheWay -> Arrived stays open - that is
    // how the group reaches the point where scanning is possible.
    if (isGroupGatedAction(action) && !scanState.canTransition) {
      notifyGroupScanIncomplete()
      return
    }

    // BD-8589: one confirmation for the group when any of its orders has no array_barcodes.
    if (requiresConfirm(groupOrder.orders, action)) {
      requestConfirm(() => void performAction(action))
      return
    }

    await performAction(action)
  }

  async function performAction(action: string) {
    await moveOrdersTogether({
      orders: groupOrder.orders,
      action,
      updateStatus,
      updateOrderAcknowledgement,
      enqueueInitials: isTmInitialsEnabled ? enqueueInitials : undefined,
    })
  }

  const firstOrder = groupOrder.orders[0]
  const statusDisplayState = quickButtonStates?.[firstOrder.status]
  const primaryAction = statusDisplayState?.quickButtons?.[0]
  const statusColor = statusDisplayState?.color

  async function handlePrimaryAction() {
    if (primaryAction) {
      await handleAction(primaryAction.action)
    }
  }

  return {
    primaryAction,
    handlePrimaryAction,
    handleAction,
    statusColor,
    scanState,
  }
}
